import React, { useCallback, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Drawer, useMediaQuery } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as Funnel } from 'src/Images/icons/common/funnel.svg';
import { ReactComponent as BulletPoints } from 'src/Images/icons/common/bulletpoints.svg';
import { ReactComponent as ExcelIcon } from 'src/Images/icons/common/excel.svg';
import { ReactComponent as Refresh } from 'src/Images/icons/common/refresh.svg';
import { ReactComponent as Search } from 'src/Images/icons/common/search.svg';
import { ReactComponent as ArrowLeft } from 'src/Images/icons/common/arrowleft.svg';
import { ReactComponent as ArrowRight } from 'src/Images/icons/common/arrowright.svg';
import { ReactComponent as Calendar } from 'src/Images/icons/common/calendar.svg';
import Table from 'src/components/Vehicle/List/Table';
import TableRightDrawer from 'src/components/Vehicle/List/TableRightDrawer';
import Breadcrumb from 'src/components/Breadcrumb';
import { CustomToolTip } from 'src/utils/CustomToolTip';
import getColorCode from 'src/utils/GetThemeColor';
import usePageTitle from 'src/utils/SetPageTitle';
import { useSetBreadcrumbData } from 'src/utils/SetBreadcrumbData';
import { getInwardReport } from 'src/redux/Actions/VehicleTracking';
import 'src/style/tableList.css';
import { useLocation } from 'react-router-dom';
import { loadStyledExcelExportModules } from 'src/utils/excelExport';

const DEFAULT_FIELDS = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
  '13',
  '14',
  '15',
  '16',
  '17',
];
const PREFERENCE_KEY = 'savedInwardReportColumns';

const today = new Date().toISOString().split('T')[0];
const emptyFilters = {
  startDate: today,
  endDate: today,
  vendorName: '',
  vehicleNumber: '',
  poNumber: '',
};

function renderCellValue(value) {
  if (value === null || value === undefined || value === '') return '-';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '-';
  if (typeof value === 'object') return value.name ?? '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return value;
}

function InwardReportTableContent({ headers, rows }) {
  if (!rows.length) {
    return (
      <tr>
        <td colSpan={headers.length || 1} className="text-center text-grey">
          No records found
        </td>
      </tr>
    );
  }

  return rows.map((row) => (
    <tr key={row.vehicle_tracking_number}>
      {headers.map((header) => (
        <td key={header.columnId} className="text-sm">
          {renderCellValue(row[header.value])}
        </td>
      ))}
    </tr>
  ));
}

function InwardReportFilterFields({
  formData,
  handleDateFilterChange,
  handleTextFilterChange,
}) {
  return (
    <>
      <div className="filter-list-item div-flex-column rg-5">
        <span className="text-sm text-semi-bold">Start Date</span>
        <div className="input-wrapper">
          <input
            type="date"
            name="startDate"
            value={formData.startDate || ''}
            onChange={handleDateFilterChange}
            className="form-input-section input-icon-date"
          />
          <span className="form-input-section-icon">
            <Calendar className="icon-size-sm" />
          </span>
        </div>
      </div>
      <div className="filter-list-item div-flex-column rg-5">
        <span className="text-sm text-semi-bold">End Date</span>
        <div className="input-wrapper">
          <input
            type="date"
            name="endDate"
            value={formData.endDate || ''}
            onChange={handleDateFilterChange}
            className="form-input-section input-icon-date"
          />
          <span className="form-input-section-icon">
            <Calendar className="icon-size-sm" />
          </span>
        </div>
      </div>
      <div className="filter-list-item div-flex-column rg-5">
        <span className="text-sm text-semi-bold">Vendor Name</span>
        <div className="input-wrapper">
          <input
            type="text"
            name="vendorName"
            placeholder="Vendor Name"
            value={formData.vendorName || ''}
            onChange={handleTextFilterChange}
            className="form-input-section"
          />
        </div>
      </div>
      <div className="filter-list-item div-flex-column rg-5">
        <span className="text-sm text-semi-bold">Vehicle Number</span>
        <div className="input-wrapper">
          <input
            type="text"
            name="vehicleNumber"
            placeholder="Vehicle Number"
            value={formData.vehicleNumber || ''}
            onChange={handleTextFilterChange}
            className="form-input-section"
          />
        </div>
      </div>
      <div className="filter-list-item div-flex-column rg-5">
        <span className="text-sm text-semi-bold">PO Number</span>
        <div className="input-wrapper">
          <input
            type="text"
            name="poNumber"
            placeholder="PO Number"
            value={formData.poNumber || ''}
            onChange={handleTextFilterChange}
            className="form-input-section"
          />
        </div>
      </div>
    </>
  );
}

export default function InwardReport() {
  usePageTitle('Inward Report');
  const location = useLocation();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width:767px)');
  const authReducer = useSelector((state) => state.AuthReducer);
  const inwardReportList = useSelector(
    (state) => state.VehicleTrackingReducer.inwardReport,
  );
  const themeColor = authReducer?.userData?.tenant?.theme;
  const tenantOrganisationId = authReducer?.userData?.organisations?.id;

  const [pageNumber, setPageNumber] = useState(1);
  const [pageData, setPageData] = useState(10);
  const [showFunnel, setShowFunnel] = useState(false);
  const [openOrganizer, setOpenOrganizer] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [formData, setFormData] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(true);

  const savedPreferences = useMemo(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(PREFERENCE_KEY));
      return {
        selected: saved?.selected || DEFAULT_FIELDS,
        order: saved?.order || [],
      };
    } catch {
      return { selected: DEFAULT_FIELDS, order: [] };
    }
  }, []);
  const [selectedFieldFilter, setSelectedFieldFilter] = useState(
    savedPreferences.selected,
  );
  const [draggedFieldOrder, setDraggedFieldOrder] = useState(
    savedPreferences.order,
  );

  const fieldFilter = inwardReportList?.fieldMapping || [];
  const rows = inwardReportList?.inwardReport || [];
  const totalPages = inwardReportList?.totalPages || 1;
  const selectedFields = inwardReportList?.selectedFields || [];
  const effectiveFieldOrder =
    draggedFieldOrder.length > 0 ? draggedFieldOrder : selectedFieldFilter;

  const tableHeaders = useMemo(
    () =>
      effectiveFieldOrder
        .map((fieldId) => {
          const selectedField = selectedFields.find(
            (field) => String(field.id) === String(fieldId),
          );
          return selectedField
            ? { ...selectedField, id: fieldId, columnId: fieldId }
            : null;
        })
        .filter(Boolean),
    [effectiveFieldOrder, selectedFields],
  );

  useSetBreadcrumbData(location.pathname, 'Inward Report');

  const debouncedSearch = useCallback(() => {
    const timer = setTimeout(() => {
      setSearchInput(searchText);
      setPageNumber(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  React.useEffect(debouncedSearch, [debouncedSearch]);

  const fetchInwardReport = useCallback(() => {
    if (!tenantOrganisationId) return;
    const params = new URLSearchParams();
    if (hasAppliedFilters) {
      if (appliedFilters.startDate) {
        params.append('start_date', appliedFilters.startDate);
      }
      if (appliedFilters.endDate) {
        params.append('end_date', appliedFilters.endDate);
      }
      if (appliedFilters.vendorName) {
        params.append('vendor_name', appliedFilters.vendorName);
      }
      if (appliedFilters.vehicleNumber) {
        params.append('vehicle_number', appliedFilters.vehicleNumber);
      }
      if (appliedFilters.poNumber) {
        params.append('po_number', appliedFilters.poNumber);
      }
    }

    if (searchInput) {
      params.append('search_term', searchInput);
    }

    dispatch(
      getInwardReport(
        tenantOrganisationId,
        pageNumber,
        searchInput,
        null,
        null,
        effectiveFieldOrder,
        pageData,
        params.toString(),
      ),
    );
  }, [
    appliedFilters,
    dispatch,
    effectiveFieldOrder,
    hasAppliedFilters,
    pageNumber,
    pageData,
    refreshKey,
    searchInput,
    tenantOrganisationId,
  ]);

  React.useEffect(() => {
    fetchInwardReport();
  }, [fetchInwardReport]);

  React.useEffect(() => {
    localStorage.setItem(
      PREFERENCE_KEY,
      JSON.stringify({
        selected: selectedFieldFilter,
        order: draggedFieldOrder,
      }),
    );
  }, [draggedFieldOrder, selectedFieldFilter]);

  const toggleOrganizer = (open) => () => setOpenOrganizer(open);

  const handleSelectedFieldFilter = (event, value) => {
    setSelectedFieldFilter((current) =>
      event.target.checked
        ? [...new Set([...current, value])]
        : current.filter((field) => field !== value),
    );
  };

  const handleDateFilterChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleTextFilterChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleResetFilter = () => {
    setFormData(emptyFilters);
    setAppliedFilters(emptyFilters);
    setHasAppliedFilters(false);
    setPageNumber(1);
  };

  const handleApplyFilter = () => {
    setAppliedFilters(formData);
    setHasAppliedFilters(true);
    setPageNumber(1);
    setShowFunnel(false);
  };

  const exportToExcel = async () => {
    if (!rows.length) return;
    const { utils, write, saveAs } = await loadStyledExcelExportModules();

    const excelData = rows.map((row) => {
      const excelRow = {};
      tableHeaders.forEach((header) => {
        const cellValue = row[header.value];
        excelRow[header.label] = Array.isArray(cellValue)
          ? cellValue.join('\r\n')
          : cellValue ?? '';
      });
      return excelRow;
    });

    const headerRow = tableHeaders.map((header) => header.label);
    const dataRows = excelData.map((row) =>
      headerRow.map((label) => row[label] ?? ''),
    );

    const formattedToday = today.replaceAll('-', '/');
    const reportStart = appliedFilters.startDate || today;
    const reportEnd = appliedFilters.endDate || formattedToday;

    const sheetContent = [
      [authReducer.userData.organisations?.name || 'Organisation Name'],
      ['Inward Register'],
      [`Reporting Period: ${reportStart} to ${reportEnd}`],
      [],
      headerRow,
      ...dataRows,
    ];

    const worksheet = utils.aoa_to_sheet(sheetContent);
    const totalColumns = headerRow.length;

    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: totalColumns - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: totalColumns - 1 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: totalColumns - 1 } },
    ];

    worksheet['!cols'] = headerRow.map((_, colIndex) => {
      const maxLen = [headerRow, ...dataRows].reduce((max, row) => {
        const cell = row[colIndex];
        const value = typeof cell === 'string' ? cell : String(cell ?? '');
        const lines = value.split('\r\n');
        const longestLine = Math.max(...lines.map((line) => line.length));
        return Math.max(max, longestLine);
      }, 10);
      return { wch: maxLen + 2 };
    });

    worksheet['!rows'] = sheetContent.map((row) => ({
      hpt:
        20 +
        (row.some((cell) => typeof cell === 'string' && cell.includes('\r\n'))
          ? 20
          : 0),
    }));

    const range = utils.decode_range(worksheet['!ref']);
    for (let R = range.s.r; R <= range.e.r; R += 1) {
      for (let C = range.s.c; C <= range.e.c; C += 1) {
        const cellRef = utils.encode_cell({ r: R, c: C });
        const cell = worksheet[cellRef];
        if (cell) {
          const style = {
            alignment: {
              wrapText: true,
              vertical: 'middle',
              horizontal: 'left',
            },
            font: {
              name: 'Calibri',
              sz: 11,
            },
            border: {
              top: { style: 'thin', color: { rgb: '000000' } },
              bottom: { style: 'thin', color: { rgb: '000000' } },
              left: { style: 'thin', color: { rgb: '000000' } },
              right: { style: 'thin', color: { rgb: '000000' } },
            },
          };

          if (R === 0 || R === 1 || R === 2) {
            style.font.bold = true;
            style.font.sz = 14;
            style.alignment.horizontal = 'left';
          }

          if (R === 4) {
            style.font.bold = true;
            style.fill = { fgColor: { rgb: 'DCE6F1' } };
            style.alignment.horizontal = 'center';
          }

          if (R > 4) {
            style.alignment.horizontal = 'center';
          }

          cell.s = style;
        }
      }
    }

    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, 'Inward Report');

    const excelBuffer = write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

    saveAs(blob, `Inward_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleInputChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (!Number.isNaN(value) && value >= 1 && value <= totalPages) {
      setPageNumber(value);
    }
  };

  const handlePreviousPage = () => {
    if (pageNumber > 1) setPageNumber(pageNumber - 1);
  };

  const handleNextPage = () => {
    if (pageNumber < totalPages) setPageNumber(pageNumber + 1);
  };

  const formattedPageNumber = pageNumber < 10 ? `0${pageNumber}` : pageNumber;
  const hasUnappliedFilters = () =>
    Object.keys(formData).some((key) => formData[key] !== appliedFilters[key]);

  return (
    <div className="div-flex-column" data-testid="inward-report-page">
      <div className="page-layout div-flex-column">
        <div className="div-space-between slider-padding left-padding">
          <div className="div-flex-row cg-5">
            <Breadcrumb />
          </div>
          <Col xxl={2} xl={2} lg={4} md={5} sm={7} xs={7} className="p-0 m-0">
            <div className="input-wrapper">
              <input
                type="text"
                data-testid="inward-report-vehicle-search"
                className="form-input-section input-icon-remove"
                placeholder="Search vehicle number"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
              <span className="form-input-section-icon">
                <Search
                  className="icon-size-lg"
                  color={getColorCode(themeColor)}
                />
              </span>
            </div>
          </Col>
        </div>
        <div className="div-space-between border-bottom slider-padding">
          <span className="text-xxl text-bold left-padding">Inward Report</span>
          <div className="div-flex-row div-align-center cg-20">
            <div className="div-flex-row cg-20 div-align-center">
              <div className="list-container div-flex-column rg-5">
                <CustomToolTip title="Filter" placement="bottom">
                  <button
                    data-testid="filter-open-btn"
                    className="icon-btn-none"
                    aria-label="filter"
                    onClick={() => {
                      setShowFunnel((current) => !current);

                      if (isMobile) {
                        setShowFunnel(true);
                      }
                    }}
                  >
                    <Funnel
                      className="icon-size-lg"
                      color={showFunnel ? getColorCode(themeColor) : '#9F9F9F'}
                    />
                  </button>
                </CustomToolTip>
              </div>
              <div className="button-container">
                <CustomToolTip title="Organiser" placement="bottom">
                  <button
                    data-testid="organizer-open-btn"
                    className="icon-btn-none"
                    aria-label="organizer"
                    onClick={toggleOrganizer(true)}
                  >
                    <BulletPoints className="icon-size-lg" color="#9F9F9F" />
                  </button>
                </CustomToolTip>
              </div>
              <Drawer
                anchor="right"
                open={openOrganizer}
                onClose={toggleOrganizer(false)}
                ModalProps={{ keepMounted: false }}
                sx={{ zIndex: 2 }}
              >
                <div className="right-drawer-component-small">
                  <TableRightDrawer
                    toggleDrawer={toggleOrganizer}
                    fieldFilter={fieldFilter}
                    selectedFieldFilter={selectedFieldFilter}
                    fieldOrder={effectiveFieldOrder}
                    setDraggedFieldOrder={setDraggedFieldOrder}
                    handleSelectedFieldFilter={handleSelectedFieldFilter}
                  />
                </div>
              </Drawer>
              <div className="button-container">
                <CustomToolTip title="Export Excel" placement="bottom">
                  <button
                    aria-label="export excel"
                    className="icon-btn-none"
                    onClick={exportToExcel}
                  >
                    <ExcelIcon className="icon-size-lg" color="#9F9F9F" />
                  </button>
                </CustomToolTip>
              </div>
              {!isMobile && (
                <CustomToolTip title="Refresh" placement="bottom">
                  <button
                    aria-label="refresh"
                    className="icon-btn-none"
                    onClick={() => setRefreshKey((current) => current + 1)}
                  >
                    <Refresh className="icon-size-lg" color="#9F9F9F" />
                  </button>
                </CustomToolTip>
              )}
            </div>
            {!isMobile && (
              <select
                value={pageData}
                onChange={(event) => {
                  setPageData(Number(event.target.value));
                  setPageNumber(1);
                }}
                className="filter-input-section"
              >
                {[10, 20, 30, 40, 50, 100, 500, 1000].map((size) => (
                  <option value={size} key={size}>
                    {size}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
        {isMobile && (
          <Drawer
            anchor="right"
            open={showFunnel}
            onClose={() => setShowFunnel(false)}
            ModalProps={{
              keepMounted: false,
            }}
            sx={{ zIndex: 2 }}
          >
            <div className="right-drawer-component-small">
              <div className="div-flex-column div-space-between rg-20">
                <div className="filter-list-item-container-scroll div-flex-column">
                  <InwardReportFilterFields
                    formData={formData}
                    handleDateFilterChange={handleDateFilterChange}
                    handleTextFilterChange={handleTextFilterChange}
                  />
                </div>
                <div className="div-flex-row div-space-between div-align-center border-top filter-list-item footer-content-for-component">
                  <button
                    className={`common-btn-outlined border-${themeColor}`}
                    onClick={handleResetFilter}
                    data-testid="filter-reset-btn"
                  >
                    Reset
                  </button>
                  <button
                    className={`common-btn theme-background-${themeColor}`}
                    onClick={handleApplyFilter}
                    data-testid="filter-apply-btn"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </Drawer>
        )}
        <Row className="g-0">
          <Col
            xxl={showFunnel ? 10 : 12}
            xl={showFunnel ? 9 : 12}
            lg={showFunnel ? 8 : 12}
            md={showFunnel ? 8 : 12}
            sm={showFunnel ? 0 : 12}
            xs={showFunnel ? 0 : 12}
            className="border-bottom"
          >
            <Table
              headers={tableHeaders}
              minCellWidth={150}
              tableContent={
                <InwardReportTableContent headers={tableHeaders} rows={rows} />
              }
            />
            <div className="footer-container">
              <div className="div-space-between div-align-center footer-content-for-page">
                <div className="div-flex-row cg-20 pagination-container div-align-center">
                  <div className="div-flex-row cg-10 div-align-center">
                    <button
                      data-testid="pagination-prev"
                      className="icon-btn-none"
                      onClick={handlePreviousPage}
                      aria-label="previous page"
                    >
                      <ArrowLeft
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                      />
                    </button>
                    <input
                      className="page-number-container input-icon-remove text-sm"
                      data-testid="pagination-input"
                      value={formattedPageNumber}
                      onChange={handleInputChange}
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      max={totalPages}
                    />
                    <button
                      data-testid="pagination-next"
                      className="icon-btn-none"
                      onClick={handleNextPage}
                      aria-label="next page"
                    >
                      <ArrowRight
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                      />
                    </button>
                  </div>
                  <span className="text-grey text-sm">
                    Page {pageNumber} of {totalPages}
                  </span>
                </div>
              </div>
            </div>
          </Col>
          {showFunnel && !isMobile && (
            <Col
              xxl={showFunnel ? 2 : 0}
              xl={showFunnel ? 3 : 0}
              lg={showFunnel ? 4 : 0}
              md={showFunnel ? 4 : 0}
              sm={showFunnel ? 12 : 0}
              xs={showFunnel ? 12 : 0}
              className="border-left border-bottom"
            >
              <div
                className="filter-list-item div-flex-row div-space-between div-align-center border-bottom"
                data-testid="filter-drawer"
              >
                <span className="text-sm text-semi-bold">Filter</span>
                {hasUnappliedFilters() && (
                  <div className="text-yellow text-xs text-semi-bold">
                    ⚠️ Unapplied Filters
                  </div>
                )}
              </div>
              <div className="div-flex-column div-space-between rg-20">
                <div className="filter-list-item-container-scroll div-flex-column">
                  <InwardReportFilterFields
                    formData={formData}
                    handleDateFilterChange={handleDateFilterChange}
                    handleTextFilterChange={handleTextFilterChange}
                  />
                </div>
                <div className="div-flex-row div-space-between div-align-center border-top filter-list-item footer-content-for-component">
                  <button
                    className={`common-btn-outlined border-${themeColor}`}
                    onClick={handleResetFilter}
                    data-testid="filter-reset-btn"
                  >
                    Reset
                  </button>
                  <button
                    className={`common-btn theme-background-${themeColor}`}
                    onClick={handleApplyFilter}
                    data-testid="filter-apply-btn"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </Col>
          )}
        </Row>
      </div>
    </div>
  );
}
